/**
 * Loadout & pods (08 §2.5), the signature pre-mission screen: armory on the left, the ranger
 * and the pods as containers in the centre, weight, cost, needs and warnings on the right.
 * A view of model.ts: every press makes a new `LoadoutState` and repaints. DEPLOY hands the
 * loadout to `GameApi.deploy` / `deployClassic`, which validates and charges; whatever it throws
 * is shown as written. Warnings never block (standard issue is always a valid loadout).
 * An operation also has its stock of specialist gear (gear.ts): taken here, on the ranger only.
 */

import './loadout.css';
import type { Deployment, PriceList } from '../../../meta/api';
import type { RangerRecord, SpecialItem } from '../../../meta/types';
import type { Ctx, MissionPlan, Screen } from '../context';
import { box, button, el, errorMessage } from '../dom';
import { serviceName } from '../text';
import { frame } from '../widgets/frame';
import { armoryList } from './armory';
import { blockText, containersView, containerTitle } from './containers';
import type { Stock } from './gear';
import { adjustGear, emptyGear, gearBlock, gearBlockText, unitWeight } from './gear';
import { gearView } from './gearView';
import type { ContainerIndex, LoadoutState, PackItem } from './model';
import { addBlock, adjust, carriedWeight, initialLoadout, podCount, toLoadout } from './model';
import { summaryView } from './summary';

/** A screen that can only say what is wrong and go back. */
function deadEnd(title: string, message: string, back: () => void): Screen {
  const f = frame('loadout', title);
  const backButton = button('BACK', back);
  f.body.append(box('menu-col menu-grow', el('div', 'menu-error menu-prose', message), box('menu-row', backButton)));
  return { el: f.el, back, initialFocus: () => backButton };
}

function findRanger(ctx: Ctx, plan: MissionPlan, id: string): RangerRecord | undefined {
  const profile = ctx.api.profile();
  const pool = plan.mode === 'classic' ? profile.classicRoster : (profile.operation?.company ?? []);
  return pool.find((r) => r.id === id);
}

export function loadoutScreen(ctx: Ctx, plan: MissionPlan): Screen {
  const TITLE = 'LOADOUT & PODS';
  const back = (): void => {
    if (plan.mode === 'operation') ctx.nav.rangerSelect(plan);
    else ctx.nav.briefing(plan);
  };

  const rangerId = plan.rangerId;
  const node = plan.node;
  if (plan.mode === 'practice' || plan.mode === 'daily') {
    const what = plan.mode === 'daily' ? 'THE DAILY DROP' : 'PRACTICE';
    return deadEnd(TITLE, `${what} FLIES STANDARD ISSUE: THERE IS NOTHING TO PACK.`, back);
  }
  if (rangerId === null) return deadEnd(TITLE, 'NO RANGER HAS BEEN SELECTED FOR THIS MISSION.', back);
  if (plan.mode === 'operation' && node === null) {
    return deadEnd(TITLE, 'THIS MISSION IS NOT ON THE THEATRE MAP: THERE IS NOTHING TO DEPLOY TO.', back);
  }

  let prices: PriceList;
  try {
    prices = ctx.api.prices(rangerId);
  } catch (err) {
    return deadEnd(TITLE, errorMessage(err), back);
  }

  const free = plan.mode === 'classic';
  const rp = free ? 0 : (ctx.api.profile().operation?.rp ?? 0);
  const pods = podCount(prices, plan.params.modifiers, plan.params.intel);
  const name = (item: PackItem): string => ctx.describe(item).name;
  const ranger = findRanger(ctx, plan, rangerId);
  // Specialist gear comes out of the operation's stock: Classic has none to draw on.
  let stock: Stock | null = null;
  let stockError = '';
  if (!free) {
    try {
      stock = ctx.api.stock();
    } catch (err) {
      stockError = errorMessage(err);
    }
  }
  const fresh = (): LoadoutState =>
    stock === null ? initialLoadout(pods) : { ...initialLoadout(pods), gear: emptyGear() };
  let state: LoadoutState = fresh();

  const f = frame('loadout', TITLE);
  f.info.append(el('span', '', ctx.describe(plan.params.archetype).name));
  f.info.append(el('span', '', free ? 'FREE PACKING' : `RP ${rp}`));

  const summary = summaryView({
    rangerName: ranger === undefined ? rangerId : serviceName(ranger),
    archetype: plan.params.archetype,
    prices,
    free,
    rp,
    name,
  });
  const containers = containersView({
    pods,
    prices,
    name,
    blockOf: (s, container, item) => addBlock(s, container, item, prices, rp, free),
    onStep: (container, item, delta) => step(container, item, delta),
  });

  const held = stock;
  const gear = held === null ? null : gearView({
    stock: held,
    describe: (item) => ctx.describe(item),
    weightOf: unitWeight,
    blockOf: (s, item) => gearBlock(s.gear ?? emptyGear(), item, held, carriedWeight(s, prices), prices.carryCap),
    onStep: (item, delta) => stepGear(held, item, delta),
  });

  const update = (): void => {
    containers.update(state);
    gear?.update(state);
    summary.update(state);
  };

  function stepGear(from: Stock, item: SpecialItem, delta: 1 | -1): void {
    const before = state.gear ?? emptyGear();
    const weight = carriedWeight(state, prices);
    const after = adjustGear(before, item, delta, from, weight, prices.carryCap);
    if (after === before) {
      const block = delta === 1 ? gearBlock(before, item, from, weight, prices.carryCap) : null;
      summary.setError(block === null ? '' : `${ctx.describe(item).name}: ${gearBlockText(block)}`);
      return;
    }
    state = { ...state, gear: after };
    summary.setError('');
    update();
  }

  function step(container: ContainerIndex, item: PackItem, delta: 1 | -1): void {
    const next = adjust(state, container, item, delta, prices, rp, free);
    if (next === state) {
      // The press changed nothing: say why, where a keyboard player will see it.
      const block = delta === 1 ? addBlock(state, container, item, prices, rp, free) : null;
      summary.setError(block === null ? '' : `${containerTitle(container)}: ${blockText(block)}`);
      return;
    }
    state = next;
    summary.setError('');
    update();
  }

  const deploy = (): void => {
    summary.setError('');
    let deployment: Deployment;
    try {
      const loadout = toLoadout(state);
      deployment = free || node === null
        ? ctx.api.deployClassic(plan.params, rangerId, loadout)
        : ctx.api.deploy(node.id, rangerId, loadout);
    } catch (err) {
      summary.setError(errorMessage(err));
      return;
    }
    ctx.fly(deployment);
  };

  const reset = (): void => {
    state = fresh();
    summary.setError('');
    update();
  };

  const deployButton = button('DEPLOY', deploy);
  const actions = box(
    'menu-load-actions',
    button('STANDARD ISSUE', reset, 'menu-btn-small'),
    box('menu-row menu-load-actions-main', deployButton, button('BACK', back)),
  );
  f.body.append(
    armoryList(prices, ctx.describe, free),
    box('menu-load-centre menu-scroll', containers.el, ...(gear === null ? [] : [gear.el])),
    box('menu-col menu-load-right', summary.el, actions),
  );
  update();
  if (stockError !== '') summary.setError(stockError);

  return {
    el: f.el,
    back,
    // Standard issue is always a valid loadout: two inputs to deploy (08 §5.3).
    initialFocus: () => deployButton,
  };
}
